import { Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BottomNav, OfflineBanner, SideNav, Skeleton, useDefaultNav } from '@/ds'
import type { NavProps } from '@/ds'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { isMock } from '@/services/config'
import { guiaService } from '@/services/guia'
import { LinkExpired } from '../screens/LinkExpired/LinkExpired'
import { Offline } from '../screens/Offline/Offline'
import { StayProvider, useStay } from '../stay/StayProvider'
import { useOnline } from '../stay/useOnline'
import { useTextLarge } from '../stay/useTextLarge'
import { useMediaQuery } from '../stay/useMediaQuery'
import './AppShell.css'

const TAB_BY_PATH: Array<[string, string]> = [
  ['/checkin', 'checkin'],
  ['/reserva', 'booking'],
  ['/accesos', 'access'],
  ['/guia', 'guide'],
  ['/ayuda', 'help'],
]

/**
 * Flujos fuera de /checkin que ocultan la navegación (bloque 3); los cambios de reserva y la cancelación (bloque 5);
 * confirmar y pagar extras y la nueva petición (bloque 6, D50).
 */
const FLOW_PATHS = ['/reserva/pagos/autorizar', '/reserva/fianza/reservar', '/reserva/extras/confirmar', '/ayuda/peticiones/nueva']
const FLOW_PREFIXES = ['/reserva/cambiar/', '/reserva/cancelar']

function activeTab(pathname: string): string {
  return TAB_BY_PATH.find(([p]) => pathname === p || pathname.startsWith(`${p}/`))?.[1] ?? 'home'
}

function Shell() {
  const { t } = useTranslation()
  const { status } = useStay()
  const online = useOnline()
  const { pathname } = useLocation()
  const { search, onLinkClick } = useAppNavigate()
  const defaults = useDefaultNav()
  useTextLarge()
  const desktop = useMediaQuery('(min-width: 1024px)')
  const guideKey = status.kind === 'ready' ? `nav-guide:${status.stay.guestCode}` : 'nav-guide:none'
  const [guide] = useAsync(guideKey, (signal) => (status.kind === 'ready' ? guiaService.getGuide(status.stay, signal) : Promise.resolve(null)))
  // Una guía sin manuales ni normas no aporta una pestaña útil. Ante un error la conservamos para no ocultar
  // accidentalmente contenido que puede volver al reintentar la carga de la reserva.
  const hasGuide = guide.kind === 'error' || (guide.kind === 'ready' && guide.value !== null && guide.value.sections.length > 0)
  const items = defaults
    .filter((it) => it.id !== 'guide' || hasGuide)
    .map((it) => ({ ...it, href: withQuery(it.href ?? '/', search, { volver: null }) }))
  const active = activeTab(pathname)
  const onSelect: NonNullable<NavProps['onSelect']> = (id, e) => {
    const target = defaults.find((it) => it.id === id)?.href ?? '/'
    onLinkClick(target)(e)
  }
  let content: React.ReactNode
  if (status.kind === 'loading') {
    content = (
      <div className="shell__loading" aria-busy="true">
        <span className="visually-hidden">{t('v2.comun.unMomento')}</span>
        <Skeleton height={220} radius={16} />
        <Skeleton height={64} radius={16} />
        <Skeleton height={64} radius={16} />
      </div>
    )
  } else if (status.kind === 'error') {
    content = status.code === 'offline' ? <Offline /> : <LinkExpired code={status.code} guestCode={status.guestCode} />
  } else {
    content = <Outlet />
  }

  // Los flujos ocultan la navegación (Mapa de navegación del bundle; D16).
  const inFlow = active === 'checkin' || FLOW_PATHS.includes(pathname) || FLOW_PREFIXES.some((p) => pathname.startsWith(p))
  const withNav = status.kind === 'ready' && !inFlow
  return (
    <div className={withNav ? 'shell shell--nav' : 'shell'}>
      {!online && <OfflineBanner className="shell__offline" />}
      <div className="shell__body">
        {withNav && desktop && <SideNav items={items} active={active} onSelect={onSelect} className="shell__side" />}
        <div className="shell__main">{content}</div>
      </div>
      {withNav && !desktop && <BottomNav items={items} active={active} onSelect={onSelect} className="shell__bottom" />}
    </div>
  )
}

/** Carcasa de la app: resuelve el enlace, aplica el tema, muestra la navegación de cinco entradas y los estados globales. */
export function AppShell() {
  return (
    <StayProvider>
      <Shell />
    </StayProvider>
  )
}

export { isMock }
